import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CircleCheck } from 'lucide-react';

import { almacen, type Session } from '../api';
import { bienvenidaDe } from '../bienvenida';
import { menuDe } from '../menu';
import { useNegocio, useVocabulario } from '../vocabulario';

/**
 * Quién tiene abierto el almacén. Lo pone la aplicación con la sesión; nulo
 * fuera de ella, y entonces no hay a quién saludar.
 */
export const SesionActual = createContext<Session | null>(null);

/**
 * El saludo de la portada: quién eres, dónde estás y qué te espera hoy.
 *
 * Va encima de las cifras del resumen y lee **el mismo** resumen —la misma
 * consulta, guardada una vez—. Si el negocio todavía no registró lo que tiene,
 * en lugar de una lista de pendientes que serían todos sus productos, lo invita
 * a empezar por donde su menú lo deje.
 */
export function Bienvenida() {
  const session = useContext(SesionActual);
  const negocio = useNegocio();
  const vocabulario = useVocabulario();
  const resumen = useQuery({ queryKey: ['resumen'], queryFn: almacen.summary });

  if (session === null || resumen.data === undefined) return null;

  const secciones = menuDe(session.scope.permissions, negocio).flatMap((grupo) =>
    grupo.items.map((item) => item.id),
  );
  const bienvenida = bienvenidaDe({
    nombre: session.user.fullName,
    ahora: new Date(),
    resumen: resumen.data,
    secciones,
    insumos: vocabulario.insumos,
  });

  return (
    <section className="bienvenida" aria-label="Bienvenida">
      <div className="bienvenida__quien">
        <p className="bienvenida__saludo">{bienvenida.saludo}</p>
        <p className="bienvenida__donde">
          {session.scope.companyTradeName} · {session.scope.branchName}
        </p>
      </div>

      {bienvenida.invitacion !== null ? (
        /*
         * Una línea y no los botones: la tarjeta «Todavía no registraste lo
         * que tienes», justo debajo, ya los trae con cuánto se vendió sin
         * registrar. Con los dos, el 8 de octubre la portada de la botica
         * repetía la misma invitación con los mismos tres botones.
         */
        <p className="bienvenida__en-orden">
          Empieza registrando lo que tienes: abajo te decimos por dónde.
        </p>
      ) : bienvenida.pendientes.length > 0 ? (
        <ul className="bienvenida__pendientes">
          {bienvenida.pendientes.map((destino) => (
            <li key={destino.texto}>
              <a href={`#/${destino.seccion}`} className="bienvenida__pendiente">
                {destino.texto}
                <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="bienvenida__en-orden">
          <CircleCheck size={16} strokeWidth={2} aria-hidden="true" />
          Todo en orden por hoy: nada vence ni falta.
        </p>
      )}
    </section>
  );
}
